import {
  UseCategoryDataReturn,
  UseCategoryDataArgs,
  UseHomePageDataArgs,
  UseHomePageDataReturn,
  UseTopicsByScoreArgs,
} from "./types";
import { Category, MarketType } from "../../types";
import { useCubeOverviewData, useHydratedCubeData } from "../index";
import {
  filterRawDataByCategory,
  getAverageOfSidesValues,
  getTopicsWithScore,
} from "./utils";
import { filterEntriesByAttributes } from "../useHydratedCubeData/utils";

export function useCategoryData({
  currentTestId,
  rawData,
  category,
  benchmarkTitle,
  selectedFilters,
}: UseCategoryDataArgs): UseCategoryDataReturn {
  const categoryRawData = filterRawDataByCategory(category, rawData);

  const categoryValuesPerSide = useHydratedCubeData(
    categoryRawData,
    selectedFilters,
    currentTestId,
    benchmarkTitle
  );

  const categoryCubeOverviewData = useCubeOverviewData(categoryValuesPerSide);
  const categoryAverageScore = getAverageOfSidesValues(
    categoryCubeOverviewData
  );

  return {
    categoryRawData,
    categoryValuesPerSide,
    categoryCubeOverviewData,
    categoryAverageScore,
  };
}

export function useMarketItems({
  rawData,
  selectedFilters,
}: UseHomePageDataArgs) {
  const categoryRawData = filterRawDataByCategory(Category.Market, rawData);

  const allEntries = categoryRawData?.allEntries ?? [];

  const filteredEntries = selectedFilters
    ? filterEntriesByAttributes(allEntries, selectedFilters)
    : allEntries;

  return filteredEntries.map(entry => ({
    x: entry.S1V ?? 0, // S1V always X axis
    y: entry.S2V ?? 0, // S2V always Y axis
  }));
}

export function useTopicsWithScore({
  currentTestId,
  rawData,
  selectedFilters,
  benchmarkTitle,
  order,
  numberOfTopics,
  aggregatesData,
}: UseTopicsByScoreArgs) {
  const allEntries = rawData?.allEntries ?? [];

  const filteredEntries = selectedFilters
    ? filterEntriesByAttributes(allEntries, selectedFilters)
    : allEntries;

  return getTopicsWithScore(
    currentTestId,
    rawData,
    filteredEntries,
    benchmarkTitle,
    order,
    numberOfTopics,
    aggregatesData
  );
}

export function useHomePageData(
  dataArgs: UseHomePageDataArgs
): UseHomePageDataReturn {
  const organizationData = useCategoryData({
    ...dataArgs,
    category: Category.Organization,
  });
  const teamData = useCategoryData({ ...dataArgs, category: Category.Team });
  const leadershipData = useCategoryData({
    ...dataArgs,
    category: Category.Leadership,
  });

  const marketItems = useMarketItems(dataArgs);
  const challenges = useTopicsWithScore({
    ...dataArgs,
    order: "ASC",
    numberOfTopics: 5,
    aggregatesData: true,
  });

  const averagedScore =
    (organizationData.categoryAverageScore.averageScore +
      teamData.categoryAverageScore.averageScore +
      leadershipData.categoryAverageScore.averageScore) /
    3;
  const averagedBenchmark =
    (organizationData.categoryAverageScore.averageBenchmark +
      teamData.categoryAverageScore.averageBenchmark +
      leadershipData.categoryAverageScore.averageBenchmark) /
    3;

  return {
    score: averagedScore,
    benchmark: averagedBenchmark,
    scoreVsBenchmarkDifference: averagedScore - averagedBenchmark,
    categoryScores: {
      organization: organizationData.categoryAverageScore,
      team: teamData.categoryAverageScore,
      leadership: leadershipData.categoryAverageScore,
    },
    marketItems: marketItems,
    marketType: MarketType.Type1,
    challenges,
  };
}
